import styled from 'styled-components/macro';
import { FC, useState } from 'react';
import { TYPOGRAPHY, DEVICE } from '../../../assets/styles';
import { FieldInputProps, FieldMetaState } from 'react-final-form';
import { CalendarModal } from './components';
import { CalendarIcon, RightArrowIcon } from '../../../assets/svg';
import { TimeUtil } from '../../../utils';
import { SHORT_DAY_AND_SHORT_MONTH_AND_DAY_NUMBER } from '../../../constants/date';
import { useTranslation } from 'react-i18next';

interface DoubleCalendarProps extends FieldInputProps<[Date, Date]> {
  className?: string;
  firstLabel?: string;
  secondLabel?: string;
  locale?: string;
  meta?: FieldMetaState<[Date, Date]>;
}

export const DoubleCalendar: FC<DoubleCalendarProps> = ({
  className,
  defaultValue,
  value,
  onChange,
  meta,
  ...props
}) => {
  const { t } = useTranslation();
  const hasError = meta?.touched && meta?.error;

  const [isVisible, setIsVisible] = useState(false);
  const closeCalendarModal = () => setIsVisible(false);
  const toggleCalendarModal = () => setIsVisible(currentValue => !currentValue);

  const changeCalendarValue = (newValue: [Date, Date]) => {
    onChange(newValue);
    closeCalendarModal();
  };

  const hasValue = value?.length === 2;
  const hasDefault = defaultValue?.length === 2;

  const startDateText =
    (hasValue && TimeUtil.getFormatted(value[0], SHORT_DAY_AND_SHORT_MONTH_AND_DAY_NUMBER)) ||
    (hasDefault && TimeUtil.getFormatted(defaultValue[0], SHORT_DAY_AND_SHORT_MONTH_AND_DAY_NUMBER));

  const endDateText =
    (hasValue && TimeUtil.getFormatted(value[1], SHORT_DAY_AND_SHORT_MONTH_AND_DAY_NUMBER)) ||
    (hasDefault && TimeUtil.getFormatted(defaultValue[1], SHORT_DAY_AND_SHORT_MONTH_AND_DAY_NUMBER));

  const hasDates = !!startDateText && !!endDateText;

  return (
    <Root className={className}>
      <InputContainer onClick={toggleCalendarModal}>
        <Label>{t('components.doubleCalendar.selectDateRange')}</Label>
        <InputWrap $isFocused={isVisible} $hasError={hasError}>
          <CalendarIconWrap $isFocused={isVisible} $hasError={hasError}>
            <CalendarIcon />
          </CalendarIconWrap>
          {hasDates ? (
            <DateRangeText>
              <span>{startDateText}</span>
              <ArrowWrap>
                <RightArrowIcon />
              </ArrowWrap>
              <span>{endDateText}</span>
            </DateRangeText>
          ) : (
            <InputInnerText $isPlaceholder>
              {t('components.doubleCalendar.selectDateRange')}
            </InputInnerText>
          )}
        </InputWrap>
        {hasError && <ErrorText>{meta?.error}</ErrorText>}
      </InputContainer>
      <CalendarModal
        isVisible={isVisible}
        onCloseModal={closeCalendarModal}
        {...props}
        defaultValue={defaultValue}
        value={value}
        onChange={changeCalendarValue}
      />
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  width: 100%;
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;

  @media ${DEVICE.desktop} {
    max-width: 620px;
  }
`;

const Label = styled.label`
  ${TYPOGRAPHY.captionRegular14}
  display: block;
  color: var(--color9);
  margin-bottom: 6px;
`;

const InputWrap = styled.div<{ $isFocused?: boolean; $hasError?: boolean }>`
  width: 100%;
  height: 56px;
  padding: 0 16px;

  display: flex;
  align-items: center;

  border: 1px solid
    ${({ $isFocused, $hasError }) =>
      $hasError ? 'var(--color12)' : $isFocused ? 'var(--color2)' : 'var(--color10)'};
  border-radius: 12px;
  box-shadow: ${({ $isFocused }) => ($isFocused ? '0 0 0 3px rgba(206, 17, 38, 0.08)' : 'none')};
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ $isFocused, $hasError }) =>
      $hasError ? 'var(--color12)' : $isFocused ? 'var(--color2)' : 'var(--color9)'};
  }
`;

const InputInnerText = styled.span<{ $isPlaceholder?: boolean }>`
  ${TYPOGRAPHY.bodyRegular16}
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? 'var(--color9)' : 'var(--color6)')};
`;

const DateRangeText = styled.div`
  ${TYPOGRAPHY.bodyRegular16}
  display: flex;
  align-items: center;
  color: var(--color6);
`;

const ArrowWrap = styled.div`
  margin: 0 8px;
  display: flex;
  align-items: center;
`;

const CalendarIconWrap = styled.div<{ $isFocused?: boolean; $hasError?: boolean }>`
  margin-right: 10px;
  & svg path {
    stroke: ${({ $isFocused, $hasError }) =>
      $hasError ? 'var(--color12)' : $isFocused ? 'var(--color2)' : 'var(--color6)'};
  }
`;

const ErrorText = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  position: absolute;
  bottom: -18px;
  left: 0;
  color: var(--color12);
`;
